import type { TranslationFunction } from "@src/packages/i18n";
import {
  FETBetaOrderLookupPendingError,
  FETBetaOrderUnavailableError,
} from "@src/packages/cycles/useFETBetaLifecycle";
import {
  CycleOutcomeWriteError,
  isClinicAuthoredOutcome,
} from "@src/packages/cycles/presenters/patientOutcomeWrite";
import { CyclePregnancyStatusWriteError } from "@src/packages/homepage/pregnancy";

/**
 * What to tell the patient when recording a beta result fails.
 *
 * The modal stays open and editable on failure, so the message is the only thing
 * that says whether trying again will help. Two rejections are recoverable and
 * were previously indistinguishable from a server outage: an earlier attempt
 * already stored a record (tapping save again now updates it), and a record the
 * clinic authored is not the patient's to overwrite (nothing here will change
 * it). Everything else stays deliberately generic — API detail is not patient
 * copy.
 *
 * Shared by the two screens that record a beta so the same failure cannot say
 * two different things depending on where the patient started.
 */
export function betaResultErrorMessage(
  error: unknown,
  t: TranslationFunction,
  /**
   * Who is looking. A supporter is refused for a different reason than the
   * patient ever is, and the refusal alone cannot tell them apart.
   */
  viewer?: { isSupporting?: boolean; cycleOwnerName?: string },
  /**
   * The record a correction was aimed at. Its provenance is the only thing that
   * says whether a refusal is genuinely about a clinic's record.
   */
  existingRecord?: { sourceKind?: string | null } | null,
): string {
  /**
   * A supporter first, whatever went wrong.
   *
   * They cannot author an outcome on any cycle — `assertOutcomeSourceAuthority`
   * accepts a patient or a provider and refuses everyone else, on both the create
   * and the correction endpoint — so no failure they meet is one a retry can
   * clear, and which failure it was does not change what to tell them.
   *
   * Checked before the FET branches for a reason. The FET lifecycle query is
   * disabled for a partner cycle, so its lookup never resolves and a supporter on
   * an FET cycle was answered "we're still loading beta tracking, try again in a
   * moment" — permanently, and about the wrong thing.
   */
  if (viewer?.isSupporting) {
    return viewer.cycleOwnerName
      ? t("care.beta_result.only_they_can_record_this_result", {
          params: { name: viewer.cycleOwnerName },
        })
      : t("care.beta_result.only_the_person_this_cycle_belongs_to_can_record");
  }
  /**
   * The cycle still has no transfer to attach the result to. The dialog asks for
   * the transfer day when none is on record and records it with the result, so
   * this only fires when that did not produce one; point her back at the date
   * she gave rather than at another screen.
   */
  if (error instanceof FETBetaOrderUnavailableError) {
    return t("care.beta_result.add_transfer_date_to_save_beta");
  }
  if (error instanceof FETBetaOrderLookupPendingError) {
    return t("care.beta_result.fet_order_lookup_pending");
  }
  if (error instanceof CycleOutcomeWriteError) {
    if (error.isConflict) return t("care.beta_result.earlier_entry_saved");
    /**
     * A refused correction means the record is not the patient's to change — but
     * only a clinic-authored one may be described that way. An ending saved
     * through "Add past cycle" carries no provenance at all, because the
     * cycle-intake schema omits it, and the correction endpoint refuses that too.
     * Calling it a clinic's record told a patient their clinic had entered
     * something they had entered themselves minutes earlier.
     *
     * Tracked for the backend as louise-life/dashboard-api#1470: until a patient's
     * own entry records them as its author, this stays unfixable in the app and
     * the honest message is that it cannot be changed here.
     */
    if (error.isForeignRecord) {
      return isClinicAuthoredOutcome(existingRecord?.sourceKind)
        ? t("care.beta_result.recorded_by_your_clinic")
        : t("care.beta_result.result_cannot_be_changed_here");
    }
  }
  return t("care.cycle_detail.alerts.error_generic");
}

/**
 * What to tell the patient when saying where the pregnancy stands fails.
 *
 * A sibling of the beta mapper rather than a branch inside it, because the two
 * writes now go to different endpoints and fail for different reasons. In
 * particular there is no clinic-authored refusal here any more: the whole point
 * of #3492 is that her answer is her own record and never a correction of the
 * clinic's result, so "recorded by your clinic" can no longer be the reason and
 * must not be offered as one.
 *
 * Two refusals are recoverable and were otherwise indistinguishable from an
 * outage: a supporter can never record this at all, and a conflict means the
 * record moved under her — the mutation invalidates the read, so saving again is
 * genuinely the fix. Everything else stays generic; API detail is not patient
 * copy.
 */
export function pregnancyStatusErrorMessage(
  error: unknown,
  t: TranslationFunction,
  viewer?: { isSupporting?: boolean; cycleOwnerName?: string },
): string {
  if (viewer?.isSupporting) {
    return viewer.cycleOwnerName
      ? t("care.update_pregnancy.only_they_can_say_where_this_stands", {
          params: { name: viewer.cycleOwnerName },
        })
      : t(
          "care.update_pregnancy.only_the_person_this_cycle_belongs_to_can_say",
        );
  }
  if (error instanceof CyclePregnancyStatusWriteError) {
    if (error.isConflict) {
      return t("care.update_pregnancy.we_have_refreshed_this_save_again");
    }
    if (error.isRefused) {
      return t(
        "care.update_pregnancy.only_the_person_this_cycle_belongs_to_can_say",
      );
    }
  }
  return t("care.cycle_detail.alerts.error_generic");
}
